import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Milk, Sparkles, CheckCircle2, Building2, Phone,
  Mail, MapPin, User, Globe, Rocket, ArrowRight, XCircle, Loader2
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { setTenantSlug } from "../lib/tenant";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const benefits = [
  "Instant 3-day free trial — fully working store",
  "Pre-loaded with milk, curd, ghee, paneer, eggs",
  "PWA customer + delivery + seller apps (App Store / Play Store ready)",
  "Subscriptions, wallet, bottle returns built-in",
  "Live tracking, route management, multi-language",
  "Razorpay & WhatsApp ready (paid addons)",
];

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function StartStore() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    business_name: "", owner_name: "", owner_email: "", owner_phone: "",
    desired_slug: "", city: "", pincode: "", estimated_customers: 100,
  });
  const [slugCheck, setSlugCheck] = useState({ checking: false, available: null, reason: "" });

  // Auto-fill slug from business name
  useEffect(() => {
    if (!f.business_name) return;
    const auto = slugify(f.business_name);
    if (!f.desired_slug || f.desired_slug === slugify(f.desired_slug)) {
      setF((cur) => ({ ...cur, desired_slug: auto }));
    }
  }, [f.business_name]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced slug availability check
  useEffect(() => {
    if (!f.desired_slug || f.desired_slug.length < 3) {
      setSlugCheck({ checking: false, available: null, reason: "" }); return;
    }
    setSlugCheck((s) => ({ ...s, checking: true }));
    const tm = setTimeout(async () => {
      try {
        const { data } = await api.get(`/api/public/slug-available/${f.desired_slug}`);
        setSlugCheck({ checking: false, available: data.available, reason: data.reason });
      } catch { setSlugCheck({ checking: false, available: null, reason: "" }); }
    }, 400);
    return () => clearTimeout(tm);
  }, [f.desired_slug]);

  const submit = async () => {
    if (!f.business_name || !f.owner_email || !f.owner_phone
        || !f.city || !f.pincode || !f.desired_slug) {
      toast.error("Please fill all required fields"); return;
    }
    if (slugCheck.available === false) {
      toast.error("That URL is taken — pick a different one"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api/public/clone-tenant", {
        business_name: f.business_name,
        owner_name: f.owner_name,
        owner_email: f.owner_email,
        owner_phone: f.owner_phone,
        desired_slug: f.desired_slug,
        city: f.city,
        pincode: f.pincode,
        estimated_customers: parseInt(f.estimated_customers) || 0,
      });
      toast.success("Your dairy is live! Logging you in…");
      // Redeem one-time token → real JWT, then route to admin dashboard
      const { data: redeemed } = await api.post("/api/public/redeem-one-time",
        { token: data.one_time_token });
      setTenantSlug(data.tenant.slug);
      login(redeemed.token, redeemed.user, null, redeemed.refresh_token || null);
      setTimeout(() => nav("/admin/dashboard"), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create your store");
    } finally { setBusy(false); }
  };

  const subdomainPreview = f.desired_slug ? `${f.desired_slug}.localappstore.in` : "yourstore.localappstore.in";
  const pathPreview = f.desired_slug ? `localappstore.in/t/${f.desired_slug}` : "localappstore.in/t/yourstore";

  return (
    <div className="min-h-screen bg-brand-cream page-enter" data-testid="start-store-page">
      <header className="sticky top-0 z-40 bg-brand-cream/90 backdrop-blur border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 rounded-2xl bg-brand-sky flex items-center justify-center">
              <Milk className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-extrabold text-xl tracking-tight">DairyFresh</span>
          </Link>
          <Link to="/" className="text-sm text-neutral-600 hover:text-brand-sky inline-flex items-center gap-1" data-testid="back-link">
            <ChevronLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10 grid md:grid-cols-2 gap-10">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-leaf/15 rounded-full text-brand-leaf text-xs font-bold uppercase tracking-wider mb-5">
            <Sparkles className="w-3.5 h-3.5" /> 3-day free trial · No card
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Launch your dairy<br/><span className="text-brand-sky">in 60 seconds</span>.
          </h1>
          <p className="text-base sm:text-lg text-neutral-700 leading-relaxed mt-5">
            We clone DairyFresh's tried-and-tested catalog, branding and operations to your own store. Edit anything later — your tenant is fully independent.
          </p>
          <ul className="mt-6 space-y-2.5">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-neutral-700" data-testid={`benefit-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-brand-leaf shrink-0 mt-0.5" /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-3xl bg-white border border-neutral-200 p-5">
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Your store URLs (auto-generated)</div>
            <div className="mt-3 space-y-2 font-mono text-sm">
              <div className="flex items-center gap-2 text-neutral-700"><Globe className="w-4 h-4 text-brand-sky shrink-0"/>{pathPreview}</div>
              <div className="flex items-center gap-2 text-neutral-700"><Globe className="w-4 h-4 text-brand-leaf shrink-0"/>{subdomainPreview}</div>
            </div>
            <div className="text-xs text-neutral-500 mt-3">Add your custom domain later when you upgrade to live.</div>
          </div>
        </div>

        <div>
          <div className="bg-white rounded-3xl border border-neutral-200 p-7">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-1">Create your store</h2>
            <p className="text-sm text-neutral-600 mb-5">Trial starts the moment you click create. No card.</p>
            <div className="space-y-3">
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-neutral-400" />
                <Input placeholder="Dairy / business name *" value={f.business_name}
                       onChange={(e) => setF({ ...f, business_name: e.target.value })}
                       className="h-13 pl-11 rounded-2xl" data-testid="ss-business" />
              </div>

              {/* Slug */}
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">Your store URL *</label>
                <div className="flex items-center gap-1 bg-brand-cream border border-neutral-200 rounded-2xl px-3 h-13">
                  <Input value={f.desired_slug}
                         onChange={(e) => setF({ ...f, desired_slug: slugify(e.target.value) })}
                         className="border-0 bg-transparent focus-visible:ring-0 px-0 h-12 font-mono"
                         placeholder="yourstore" data-testid="ss-slug"/>
                  <span className="text-sm text-neutral-500 font-mono whitespace-nowrap">.localappstore.in</span>
                  {slugCheck.checking && <Loader2 className="w-4 h-4 text-neutral-400 animate-spin shrink-0"/>}
                  {!slugCheck.checking && slugCheck.available === true && <CheckCircle2 className="w-4 h-4 text-brand-leaf shrink-0" data-testid="slug-ok"/>}
                  {!slugCheck.checking && slugCheck.available === false && <XCircle className="w-4 h-4 text-brand-clay shrink-0" data-testid="slug-bad"/>}
                </div>
                {slugCheck.available === false && (
                  <div className="text-xs text-brand-clay mt-1">{slugCheck.reason === "reserved" ? "That URL is reserved." : "That URL is already taken."}</div>
                )}
              </div>

              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-neutral-400" />
                <Input placeholder="Owner name" value={f.owner_name}
                       onChange={(e) => setF({ ...f, owner_name: e.target.value })}
                       className="h-13 pl-11 rounded-2xl" data-testid="ss-owner" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-neutral-400" />
                  <Input placeholder="Email *" value={f.owner_email}
                         onChange={(e) => setF({ ...f, owner_email: e.target.value })}
                         className="h-13 pl-11 rounded-2xl" data-testid="ss-email" />
                </div>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-neutral-400" />
                  <Input placeholder="Phone *" value={f.owner_phone}
                         onChange={(e) => setF({ ...f, owner_phone: e.target.value })}
                         className="h-13 pl-11 rounded-2xl" data-testid="ss-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-neutral-400" />
                  <Input placeholder="City *" value={f.city}
                         onChange={(e) => setF({ ...f, city: e.target.value })}
                         className="h-13 pl-11 rounded-2xl" data-testid="ss-city" />
                </div>
                <Input placeholder="Pincode *" value={f.pincode}
                       onChange={(e) => setF({ ...f, pincode: e.target.value })}
                       className="h-13 rounded-2xl" data-testid="ss-pincode" />
              </div>
              <Button onClick={submit} disabled={busy || slugCheck.available === false}
                      className="w-full h-14 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold text-base mt-2"
                      data-testid="submit-application-btn">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Cloning catalog…</> :
                        <><Rocket className="w-4 h-4 mr-2"/> Create my store (free 3 days) <ArrowRight className="w-4 h-4 ml-2"/></>}
              </Button>
              <p className="text-xs text-neutral-500 text-center">By creating, you agree to our terms. Trial expires after 3 days.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
